import React from "react";
import { DeleteDialog, Modal } from "../components";
import { NoDataAvailableDiv } from "../components/NoDataAvailableDiv";
import { Panel } from "../components/panels";
import ProductEdit from "./ProductEdit";
import ProductsTable from "./ProductsTable";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { ButtonSecondary } from "components/Atoms/Button";
import { useCustomQuery } from "utilities/hookutils";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { getProductsByParentQueryKey } from "eSign/panelsegment/ProductsPanel";
import { getPaymentsByInvoiceQueryKey } from "invoice/makepayment/PaymentsPanel";

interface Props {
  productsPanelConfig: server.dto.LinkedProductPanelConfig
  type: server.dto.DocumentTypes | "show"
  id: number
}

const ProductsPanel = (props: Props) => {
  const [editingProduct, setEditedProduct] = React.useState<server.dto.Product>(undefined);
  const [deletingProduct, setDeletingProduct] = React.useState<server.dto.Product>(undefined);

  const listQueryKey = getProductsByParentQueryKey(props.type, props.id);

  const queryClient = useQueryClient();

  const productListApi = useCustomQuery({
    queryFn: () => window.CoreApi.Product.ListByParent({
      mainArea: props.type,
      parentId: props.id
    }).then(r => r.data.data),
    queryKey: listQueryKey
  });


  const duplicateProductMutation = useMutation({
    mutationFn: async (product: server.dto.Product) => window.CoreApi.Product.Create({
      costsPricePerUnit: product.costs?.pricePerUnit.amount,
      costsVatRateId: product.costs?.vatRate.vatRateId,
      date: product.date,
      description: product.description,
      ledgerNumber: product.ledgerNumber,
      mainArea: props.type,
      parentId: props.id,
      productType: product.productType.value,
      quantity: product.quantity,
      salesPricePerUnit: product.sales?.pricePerUnit.amount,
      salesVatRateId: product.sales?.vatRate.vatRateId,
      title: product.title,
      unit: product.unit
    })
  });

  const duplicateProductMutationWithInstr = useMutation({
    mutationFn: window.CoreApi.Product.Create
  });


  const onSort = (productByParent: server.dto.ProductsByParent) => {
    queryClient.setQueryData(listQueryKey, () => productByParent);
    return window.CoreApi.Product.StoreSorted({
      mainArea: props.type,
      parentId: props.id,
      productIds: productByParent.collection.map(p => p.productId)
    });
  };

  const onProductSaved = async () => {
    setEditedProduct(undefined);
    productListApi.refetch();

    const paymentsQueryKey = getPaymentsByInvoiceQueryKey(props.id);
    queryClient.invalidateQueries({ queryKey: paymentsQueryKey })
  };

  const onDeleteClicked = (product: server.dto.Product) => {
    setDeletingProduct(product);
  };

  const onDeleted = async () => {
    const res = await window.CoreApi.Product.Delete({ productId: deletingProduct.productId });
    if (res.data.hasErrors) {
      window.displayGenericModal(RESX.ExceptionMessage.ThereIsAProblem, RESX.ExceptionMessage.ThereWasAnError, "tiny", "red");
      return;
    }

    const paymentsQueryKey = getPaymentsByInvoiceQueryKey(props.id);
    queryClient.invalidateQueries({ queryKey: paymentsQueryKey });

    setDeletingProduct(undefined);
    productListApi.refetch();
  };

  const onProductCopyClicked = async (product: server.dto.Product) => {
    if (duplicateProductMutation.isLoading) return;
    await duplicateProductMutation.mutateAsync(product);
    productListApi.refetch();
    window.refreshProducts?.();
  }


  window.refreshProducts = productListApi.refetch;

  const noData = productListApi.data == null || productListApi.data.collection.length === 0;

  const { refToScrollTo } = useShortcutPanel({
    type: "product",
    label: props.productsPanelConfig.headerLabel,
    totalItems: productListApi.data?.collection.length ?? 0,
    onAddClicked: props.productsPanelConfig.canModify ? () => { setEditedProduct(null); } : undefined
  });

  return <Panel
    panelClassName="s1_related-feature-panel"
    panelRef={refToScrollTo}
    title={props.productsPanelConfig.headerLabel}
    button={props.productsPanelConfig.canModify && <ButtonSecondary disabled={productListApi.isLoading} onClick={() => { setEditedProduct(null); }} label={RESX.Buttons.btnNew} />}
  >
    {!noData && <ProductsTable
      onEditClicked={prod => { setEditedProduct(prod); }}
      onDeleteClicked={onDeleteClicked}
      onCopyClicked={onProductCopyClicked}
      onProductsOrderChanged={onSort}
      productsByParent={productListApi.data}
      productsPanelConfig={props.productsPanelConfig}
    />}
    {noData &&
      <NoDataAvailableDiv />
    }

    <Modal
      isOpen={editingProduct !== undefined}
      title={editingProduct === null ? RESX.Product.NewProduct : RESX.Product.EditProduct}
      width="huge"
      height={780}
      handleClose={() => { setEditedProduct(undefined); }}
    >
      <ProductEdit
        initialProduct={editingProduct}
        productsConfig={props.productsPanelConfig}
        productsByParent={productListApi.data}
        onProductSaved={onProductSaved}
        saveAsDuplicateMutation={duplicateProductMutationWithInstr}
        mainArea={props.type}
      />
    </Modal>

    <DeleteDialog
      isOpen={deletingProduct != null}
      onDelete={onDeleted}
      handleClose={() => { setDeletingProduct(undefined); }}
      message={RESX.GeneralWarnings.DeleteSure}></DeleteDialog>
  </Panel>;
};

export default ProductsPanel;